import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Pagination,
  Popconfirm,
  Select,
  Spin,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import {
  BankAccount,
  DevicePingResponse,
  TRADEVEST_FAILED_STATUSES,
  TRADEVEST_PROGRESS_STATUSES,
  TradevestReminderTemplate,
  TradevestRetryResponse,
  TradevestSupportGuidanceResponse,
  TradevestUserPortfolioSummary,
  UserDetailResponse,
  UserStatus,
  UserTitle,
  MAX_TAX_ID_LENGTH,
  normalizeTaxId,
  requiresTaxId,
} from '@mygate/common';
import { usersService } from '@/services/users';
import { notificationsService } from '@/services/notifications';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime } from '@/utils/date';
import { formatFullName } from '@/utils/user';
import { ApiError } from '@/services/api';
import {
  REMINDER_TEMPLATES,
  WAITING_ON_LABELS,
  formatGuidanceTiming,
  resolveGuidanceControl,
} from '@/utils/tradevestGuidance';
import { createLatestRequestTracker } from '@/utils/latestRequest';
import { createModalSession } from '@/utils/modalSession';
import { describePushOrigin, describePushStatus, lastHistoryPage } from '@/utils/pushHistory';
import { SendNotificationModal } from './SendNotificationModal';
import { TradevestTaxModal } from './TradevestTaxModal';

const { Text } = Typography;

const STATUS_DESCRIPTIONS: Record<string, string> = {
  not_started: 'Initial state — user has not started onboarding',
  kyc_required: 'User must complete KYC before proceeding',
  tax_id_required: 'Tradevest needs a tax ID for this residency — enter it in Edit User Details to resume',
  natural_person_created:
    'NP created — waiting for bank details, or bank details are on record and the customer role is re-checked / created automatically',
  customer_created: 'Customer + Identity created — user can accept terms in app',
  documents_signing_pending: 'Documents signed (202) — waiting for webhook confirmation',
  documents_signed: 'Documents confirmed — ready for onboarding request',
  onboarding_initiated: 'Onboarding request submitted — waiting for approval',
  onboarding_approved: 'Approved — system creates Balance Account + Crypto Wallet',
  products_created: 'Product requests submitted to Tradevest',
  awaiting_products_activation: 'Waiting for product ACTIVE webhooks',
  completed: 'Fully onboarded — IBAN and wallet active',
  natural_person_creation_failed: 'Failed to create Natural Person',
  identity_verification_failed: 'Identity verification rejected or PDF missing',
  bank_account_validation_error: 'IBAN/owner validation failed — user can retry',
  natural_person_not_found_for_customer: 'NP ID not set for Customer creation',
  customer_id_extraction_failed: 'Customer exists (409) but ID extraction failed',
  customer_creation_failed: 'Tradevest API error creating Customer',
  terms_signing_failed: 'Terms signing failed',
  privacy_signing_failed: 'Privacy Policy signing failed',
  documents_signing_failed: 'Document signing rejected via webhook',
  onboarding_creation_failed: 'Failed to create onboarding request',
  onboarding_rejected: 'Tradevest rejected the onboarding',
  onboarding_invalid: 'Onboarding data validation failed',
  onboarding_timeout: 'No approval within 48h of submission',
  product_creation_failed: 'Failed to create products',
  products_activation_timeout: 'Products not ACTIVE within 4h of creation',
  unknown_error: 'Unexpected error during onboarding',
};

interface UserDetailModalProps {
  userId: string | null;
  visible: boolean;
  onClose: () => void;
  onUserUpdated?: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline-scoped styles + design tokens. Scoped under .udm-root so they don't
// leak. Mirrors the design's CSS variables from modal.jsx.
// ─────────────────────────────────────────────────────────────────────────────
const SCOPED_CSS = `
.udm-root {
  --udm-bg: #f4f5f7;
  --udm-panel: #ffffff;
  --udm-panel-2: #fafbfc;
  --udm-border: #e7e9ee;
  --udm-border-strong: #d8dbe2;
  --udm-text: #0f1217;
  --udm-text-2: #4a5160;
  --udm-text-3: #8a91a0;
  --udm-accent: #2563eb;
  --udm-accent-tint: #eff4ff;
  --udm-green: #15803d;
  --udm-green-tint: #ecfdf5;
  --udm-green-border: #bbf7d0;
  --udm-red: #b91c1c;
  --udm-red-tint: #fef2f2;
  --udm-red-border: #fecaca;
  --udm-amber: #b45309;
  --udm-amber-tint: #fffbeb;
  --udm-amber-border: #fde68a;
  --udm-slate-tint: #f1f3f7;
  --udm-radius: 14px;
  --udm-radius-sm: 8px;
  --udm-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: var(--udm-text);
  font-size: 14px;
  line-height: 1.45;
}
.udm-root *, .udm-root *::before, .udm-root *::after { box-sizing: border-box; }
.udm-root .udm-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 16px;
}
@media (max-width: 1100px) {
  .udm-root .udm-grid > div { grid-column: span 12 !important; }
}
.udm-root .udm-copy:hover { background: var(--udm-slate-tint); border-color: var(--udm-border); }
.udm-root .udm-iconbtn:hover { background: var(--udm-slate-tint); }
`;

let scopedStyleInjected = false;
function ensureScopedStyles() {
  if (scopedStyleInjected || typeof document === 'undefined') return;
  const tag = document.createElement('style');
  tag.setAttribute('data-udm', 'true');
  tag.textContent = SCOPED_CSS;
  document.head.appendChild(tag);
  scopedStyleInjected = true;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline SVG icon bag — keeps icons crisp and matches design exactly.
// ─────────────────────────────────────────────────────────────────────────────
type IconProps = React.SVGProps<SVGSVGElement>;
const Icon = {
  close: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      {...p}
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
  copy: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
    </svg>
  ),
  edit: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M14 4l6 6-11 11H3v-6L14 4z" />
    </svg>
  ),
  check: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M4 12l5 5 11-12" />
    </svg>
  ),
  alert: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      {...p}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v6M12 16.5v.5" />
    </svg>
  ),
  refresh: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  ),
  lock: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 1 1 8 0v4" />
    </svg>
  ),
  unlock: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 1 1 8 0" />
    </svg>
  ),
  ban: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      {...p}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </svg>
  ),
  bell: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </svg>
  ),
  log: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 9h8M8 13h8M8 17h5" />
    </svg>
  ),
  chart: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 19h18" />
      <path d="M5 19V11M10 19V6M15 19v-5M20 19V8" />
    </svg>
  ),
  card: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 11h18" />
    </svg>
  ),
  shield: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" />
    </svg>
  ),
  wallet: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 7a2 2 0 0 1 2-2h12v4" />
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <circle cx="17" cy="13.5" r="1.2" />
    </svg>
  ),
  device: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <rect x="2" y="5" width="14" height="10" rx="1.5" />
      <rect x="15" y="9" width="7" height="11" rx="1.5" />
      <path d="M5 18h6" />
    </svg>
  ),
  plug: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M9 7V3M15 7V3" />
      <path d="M7 7h10v4a5 5 0 0 1-10 0V7z" />
      <path d="M12 16v5" />
    </svg>
  ),
  arrowUp: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </svg>
  ),
  arrowDown: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M12 5v14" />
      <path d="M19 12l-7 7-7-7" />
    </svg>
  ),
  arrowOut: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M20 14v6H4V4h6" />
    </svg>
  ),
  globe: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  ),
  bank: (p: IconProps) => (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...p}
    >
      <path d="M3 10l9-6 9 6" />
      <path d="M5 10v8M9 10v8M15 10v8M19 10v8" />
      <path d="M3 20h18" />
    </svg>
  ),
};

// ─────────────────────────────────────────────────────────────────────────────
// Atoms
// ─────────────────────────────────────────────────────────────────────────────
type Tone = 'green' | 'red' | 'amber' | 'blue' | 'neutral';

const TONE_MAP: Record<Tone, { bg: string; fg: string; bd: string }> = {
  green: { bg: 'var(--udm-green-tint)', fg: 'var(--udm-green)', bd: 'var(--udm-green-border)' },
  red: { bg: 'var(--udm-red-tint)', fg: 'var(--udm-red)', bd: 'var(--udm-red-border)' },
  amber: { bg: 'var(--udm-amber-tint)', fg: 'var(--udm-amber)', bd: 'var(--udm-amber-border)' },
  blue: { bg: 'var(--udm-accent-tint)', fg: 'var(--udm-accent)', bd: '#cfdcfb' },
  neutral: { bg: 'var(--udm-slate-tint)', fg: 'var(--udm-text-2)', bd: 'var(--udm-border)' },
};

function Pill({
  tone = 'neutral',
  icon,
  children,
  size = 'sm',
}: {
  tone?: Tone;
  icon?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md';
}) {
  const t = TONE_MAP[tone];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        padding: size === 'sm' ? '2px 8px' : '4px 10px',
        borderRadius: 999,
        fontSize: size === 'sm' ? 11.5 : 12,
        fontWeight: 600,
        letterSpacing: 0.2,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {children}
    </span>
  );
}

function CopyField({
  value,
  mono = true,
  truncate = false,
  dim = false,
}: {
  value: string;
  mono?: boolean;
  truncate?: boolean;
  dim?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const click = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1100);
  };
  return (
    <button
      onClick={click}
      type="button"
      className="udm-copy"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px',
        background: 'transparent',
        border: '1px solid transparent',
        borderRadius: 6,
        cursor: 'pointer',
        font: 'inherit',
        fontFamily: mono ? 'var(--udm-mono)' : 'inherit',
        fontSize: mono ? 12.5 : 13.5,
        color: dim ? 'var(--udm-text-3)' : 'var(--udm-text)',
        maxWidth: '100%',
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      <span
        style={{
          overflow: truncate ? 'hidden' : 'visible',
          textOverflow: truncate ? 'ellipsis' : 'clip',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
      <span style={{ color: copied ? 'var(--udm-green)' : 'var(--udm-text-3)', display: 'inline-flex' }}>
        {copied ? <Icon.check /> : <Icon.copy />}
      </span>
    </button>
  );
}

type BtnTone = 'neutral' | 'primary' | 'danger' | 'ghost';
const BTN_STYLES: Record<BtnTone, { bg: string; fg: string; bd: string; hover: string }> = {
  neutral: { bg: '#fff', fg: 'var(--udm-text)', bd: 'var(--udm-border-strong)', hover: '#f7f8fa' },
  primary: { bg: 'var(--udm-accent)', fg: '#fff', bd: 'var(--udm-accent)', hover: '#1d56cf' },
  danger: { bg: '#fff', fg: 'var(--udm-red)', bd: 'var(--udm-red-border)', hover: 'var(--udm-red-tint)' },
  ghost: { bg: 'transparent', fg: 'var(--udm-text-2)', bd: 'transparent', hover: 'var(--udm-slate-tint)' },
};

function Btn({
  tone = 'neutral',
  icon,
  children,
  onClick,
  size = 'md',
  loading = false,
  disabled = false,
  type = 'button',
}: {
  tone?: BtnTone;
  icon?: ReactNode;
  children?: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size?: 'sm' | 'md';
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  const s = BTN_STYLES[tone];
  const [hover, setHover] = useState(false);
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={isDisabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        background: hover && !isDisabled ? s.hover : s.bg,
        color: s.fg,
        border: `1px solid ${s.bd}`,
        padding: size === 'sm' ? '5px 10px' : '7px 12px',
        borderRadius: 8,
        fontWeight: 500,
        fontSize: size === 'sm' ? 12.5 : 13,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        font: 'inherit',
        fontFamily: 'inherit',
        transition: 'background 120ms',
        whiteSpace: 'nowrap',
        opacity: isDisabled ? 0.55 : 1,
      }}
    >
      {loading ? <LoadingOutlined style={{ fontSize: 12 }} spin /> : icon}
      {children}
    </button>
  );
}

function Field({
  label,
  children,
  span = 1,
  align = 'flex-start',
}: {
  label: string;
  children: ReactNode;
  span?: number;
  align?: CSSProperties['alignItems'];
}) {
  return (
    <div style={{ gridColumn: `span ${span}`, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <div
        style={{
          fontSize: 11,
          color: 'var(--udm-text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          color: 'var(--udm-text)',
          fontSize: 13.5,
          minWidth: 0,
          display: 'flex',
          alignItems: align,
          gap: 6,
          flexWrap: 'wrap',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function SectionCard({
  title,
  icon,
  children,
  accent,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  accent?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--udm-panel)',
        border: '1px solid var(--udm-border)',
        borderRadius: 'var(--udm-radius)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100%',
      }}
    >
      <header
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--udm-border)',
          background: 'var(--udm-panel-2)',
        }}
      >
        <span
          style={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 22,
            height: 22,
            borderRadius: 6,
            background: accent || 'var(--udm-slate-tint)',
            color: 'var(--udm-text-2)',
          }}
        >
          {icon}
        </span>
        <h3
          style={{
            margin: 0,
            fontSize: 12.5,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            color: 'var(--udm-text-2)',
          }}
        >
          {title}
        </h3>
      </header>
      <div
        style={{
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          flex: 1,
        }}
      >
        {children}
      </div>
    </section>
  );
}

function TradevestGuidanceBlock({
  response,
  isAdmin,
  onRemind,
  onCopyRequest,
}: {
  response: TradevestSupportGuidanceResponse;
  isAdmin: boolean;
  onRemind: (template: TradevestReminderTemplate) => void;
  onCopyRequest: () => void;
}) {
  const { guidance } = response;
  if (!guidance) return null;
  const control = resolveGuidanceControl(guidance, isAdmin);
  const timing = formatGuidanceTiming(guidance, value => formatDateTime(value));

  const renderControl = (): ReactNode => {
    switch (control.kind) {
      case 'remind':
        return (
          <Btn tone="primary" size="sm" icon={<Icon.bell />} onClick={() => onRemind(control.template)}>
            {control.label}
          </Btn>
        );
      case 'copyRequest':
        return (
          <Btn tone="primary" size="sm" icon={<Icon.copy />} onClick={onCopyRequest}>
            {control.label}
          </Btn>
        );
      case 'note':
        return <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--udm-text)' }}>{control.text}</span>;
      case 'none':
        return null;
    }
  };

  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${guidance.overdue ? 'var(--udm-red-border)' : 'var(--udm-border)'}`,
        background: guidance.overdue ? 'var(--udm-red-tint)' : 'var(--udm-panel)',
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 11,
            color: 'var(--udm-text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
          }}
        >
          Was jetzt?
        </span>
        <Pill tone={guidance.overdue ? 'red' : 'blue'}>{WAITING_ON_LABELS[guidance.waitingOn]}</Pill>
        <span style={{ fontWeight: 600, color: 'var(--udm-text)' }}>{guidance.headline}</span>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--udm-text-2)' }}>{guidance.explanation}</div>
      {timing && (
        <div style={{ fontSize: 11.5, color: guidance.overdue ? 'var(--udm-red)' : 'var(--udm-text-3)' }}>{timing}</div>
      )}
      {control.kind !== 'none' && <div>{renderControl()}</div>}
    </div>
  );
}

const PUSH_HISTORY_QUERY_KEY = 'userPushHistory';
const PUSH_HISTORY_PAGE_SIZE = 10;

/**
 * Render with `key={userId}`: the page belongs to one customer, and a fresh mount per customer also keeps
 * `keepPreviousData` from ever showing the previous customer's pushes while the next one loads.
 */
function PushHistoryBlock({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  // Reopening the same customer is no new mount and no prop change: the closed modal keeps this block mounted and
  // does not re-render it. The modal therefore invalidates this query on every open.
  const historyQuery = useQuery({
    queryKey: [PUSH_HISTORY_QUERY_KEY, userId, page],
    queryFn: async () => {
      const response = await notificationsService.getUserPushHistory(userId, { page, limit: PUSH_HISTORY_PAGE_SIZE });
      return response.data ?? null;
    },
    // No staleTime on purpose: the page switch below must reload the page it lands on, not show its cached copy.
    placeholderData: keepPreviousData,
    retry: false,
  });
  const history = historyQuery.data;
  const lastPage = history ? lastHistoryPage(history.total, PUSH_HISTORY_PAGE_SIZE) : 1;

  useEffect(() => {
    // The TTL can drop pushes while staff pages through them. An out-of-range page comes back empty and without
    // pagination, so move to the last page that still exists, which reloads on the switch.
    if (historyQuery.isPlaceholderData || page <= lastPage) return;
    setPage(lastPage);
  }, [historyQuery.isPlaceholderData, page, lastPage]);

  if (historyQuery.isPending) return <Spin size="small" />;
  if (historyQuery.isError || !history) {
    return <span style={{ color: 'var(--udm-red)', fontSize: 12.5 }}>Could not load the push history</span>;
  }
  if (history.total === 0) return <span style={{ color: 'var(--udm-text-3)' }}>No pushes in the last 30 days</span>;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {history.notifications.map((item, index) => {
          const status = describePushStatus(item);
          return (
            <div
              key={item.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '150px minmax(0, 1fr) auto',
                gap: 12,
                alignItems: 'start',
                padding: '10px 0',
                borderTop: index === 0 ? 'none' : '1px solid var(--udm-border)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 12.5, fontWeight: 500 }}>{formatDateTime(item.createdAt)}</span>
                <span style={{ fontSize: 11.5, color: 'var(--udm-text-3)' }}>{formatRelative(item.createdAt)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span style={{ fontWeight: 600, color: 'var(--udm-text)', overflowWrap: 'anywhere' }}>
                  {item.title}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--udm-text-2)', overflowWrap: 'anywhere' }}>
                  {item.body}
                </span>
                <span style={{ fontSize: 11.5, color: 'var(--udm-text-3)' }}>{describePushOrigin(item)}</span>
              </div>
              <Tooltip title={status.hint}>
                <span>
                  <Pill tone={status.tone}>{status.label}</Pill>
                </span>
              </Tooltip>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11.5, color: 'var(--udm-text-3)' }}>Pushes are kept for 30 days.</span>
        {history.total > PUSH_HISTORY_PAGE_SIZE && (
          <Pagination
            size="small"
            simple
            current={page}
            pageSize={PUSH_HISTORY_PAGE_SIZE}
            total={history.total}
            onChange={setPage}
          />
        )}
      </div>
    </>
  );
}

function StatusTile({ label, value, tone }: { label: string; value: string; tone: Tone }) {
  const t = TONE_MAP[tone];
  return (
    <div
      style={{
        border: `1px solid ${t.bd}`,
        borderRadius: 10,
        padding: '8px 10px',
        background: t.bg,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          color: 'var(--udm-text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: t.fg }}>{value}</div>
    </div>
  );
}

function WalletRow({ chain, color, addr }: { chain: string; color: string; addr: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
      <span
        style={{
          width: 30,
          height: 26,
          borderRadius: 6,
          background: `linear-gradient(135deg, ${color}, ${color}dd)`,
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {chain}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <CopyField value={addr} truncate />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const EDIT_ICON_BUTTON_STYLE: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--udm-text-3)',
  cursor: 'pointer',
  padding: 2,
  display: 'inline-flex',
  borderRadius: 4,
};

function getInitials(firstName?: string, lastName?: string): string {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  const out = `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return out || '?';
}

function formatRelative(input?: string | Date | null): string {
  if (!input) return '';
  const d = new Date(input);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);
  const min = Math.round(abs / 60000);
  const hr = Math.round(abs / 3_600_000);
  const day = Math.round(abs / 86_400_000);
  const month = Math.round(abs / (30 * 86_400_000));
  const year = Math.round(abs / (365 * 86_400_000));
  let s: string;
  if (min < 1) s = 'just now';
  else if (min < 60) s = `${min} min${min === 1 ? '' : 's'}`;
  else if (hr < 24) s = `${hr} hour${hr === 1 ? '' : 's'}`;
  else if (day < 30) s = `${day} day${day === 1 ? '' : 's'}`;
  else if (month < 12) s = `${month} month${month === 1 ? '' : 's'}`;
  else s = `${year} year${year === 1 ? '' : 's'}`;
  if (s === 'just now') return s;
  return future ? `in ${s}` : `${s} ago`;
}

function getStatusPill(status: string): { tone: Tone; label: string; icon?: ReactNode } {
  switch (status) {
    case UserStatus.ACTIVE:
      return { tone: 'green', label: 'Active', icon: <Icon.check /> };
    case UserStatus.SUSPENDED:
      return { tone: 'red', label: 'Suspended', icon: <Icon.lock /> };
    case UserStatus.PENDING_VERIFICATION:
      return { tone: 'amber', label: 'Pending verification', icon: <Icon.alert /> };
    case UserStatus.MANUAL_REVIEW:
      return { tone: 'amber', label: 'Manual Review', icon: <Icon.alert /> };
    case UserStatus.DELETION_PENDING:
      return { tone: 'neutral', label: 'Deletion pending' };
    default:
      return { tone: 'neutral', label: status || 'Unknown' };
  }
}

function getKycPill(kycCase?: { status?: string; result?: string } | null): { tone: Tone; label: string } {
  if (!kycCase || !kycCase.status) return { tone: 'neutral', label: 'No KYC' };
  if (kycCase.status === 'CLOSED') {
    if (kycCase.result === 'SUCCESS') return { tone: 'green', label: 'KYC closed' };
    if (kycCase.result === 'DECLINED') return { tone: 'red', label: 'KYC declined' };
    return { tone: 'neutral', label: `KYC ${kycCase.status.toLowerCase()}` };
  }
  if (kycCase.status === 'IN_PROGRESS') return { tone: 'blue', label: 'KYC in progress' };
  if (kycCase.status === 'NEW' || kycCase.status === 'REQUIRED' || kycCase.status === 'PENDING') {
    return { tone: 'amber', label: 'KYC pending' };
  }
  return { tone: 'neutral', label: `KYC ${kycCase.status.toLowerCase()}` };
}

function PayCenterCardRow({ account }: { account: BankAccount }) {
  const card = account.paycenterCard;
  if (!card) return null;
  const tone: Tone =
    card.status === 'active' ? 'green' : card.status === 'blocked' || card.status === 'inactive' ? 'red' : 'amber';
  const embossing = [card.line1, card.line2].filter(Boolean).join(' / ') || '—';
  const panLine = [
    `PAN: ${card.pan}`,
    card.expirationDate ? `exp ${card.expirationDate}` : null,
    typeof card.pinTries === 'number' ? `PIN tries: ${card.pinTries}` : null,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <div
      style={{
        border: '1px solid var(--udm-border)',
        borderRadius: 10,
        background: 'var(--udm-panel-2)',
        padding: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          style={{
            fontSize: 11,
            color: 'var(--udm-text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
          }}
        >
          Card
        </span>
        <Pill tone={tone}>{card.status}</Pill>
      </div>
      <div style={{ fontSize: 12, color: 'var(--udm-text-2)' }}>
        {embossing}
        {!card.line2 && <span style={{ color: 'var(--udm-text-3)' }}> (no line 2)</span>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--udm-text-3)', fontFamily: 'var(--udm-mono)' }}>{panLine}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export function UserDetailModal({ userId, visible, onClose, onUserUpdated }: UserDetailModalProps) {
  ensureScopedStyles();

  const [loading, setLoading] = useState(false);
  const [userDetail, setUserDetail] = useState<UserDetailResponse | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showTradevestTaxModal, setShowTradevestTaxModal] = useState(false);
  const [cardValidationData, setCardValidationData] = useState<unknown | null>(null);
  const [plasticCardSatisfiedData, setPlasticCardSatisfiedData] = useState<unknown | null>(null);
  const [cardPropertiesLoading, setCardPropertiesLoading] = useState(false);
  const [showCardPropertiesModal, setShowCardPropertiesModal] = useState(false);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [editDetailsForm] = Form.useForm();
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [editEmailForm] = Form.useForm();
  const [editEmailLoading, setEditEmailLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsData, setAccountsData] = useState<BankAccount[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [registerPayCenterLoading, setRegisterPayCenterLoading] = useState(false);
  const [tradevestRetryLoading, setTradevestRetryLoading] = useState(false);
  const [tradevestRetryResult, setTradevestRetryResult] = useState<TradevestRetryResponse | null>(null);
  // Bound to the customer it was fetched for: rendering and copying both re-check `userId` matches before using it,
  // so a late answer for the previous customer can never be shown, or copied, while the next customer is open.
  const [tradevestGuidance, setTradevestGuidance] = useState<{
    userId: string;
    data: TradevestSupportGuidanceResponse;
  } | null>(null);
  const [reminderTemplate, setReminderTemplate] = useState<TradevestReminderTemplate | null>(null);
  const guidanceRequests = useRef(createLatestRequestTracker());
  // The customer open right now. Guards every fetch-then-setState continuation (guidance, retry, reset, set status,
  // edit details) so a mutation started for customer A can never refresh customer B's card once staff moved on.
  const openUserId = useRef<string | null>(null);
  // A token for the currently open customer. Every async handler below captures it at the start and re-checks it
  // after each await; a late answer for a customer staff already moved on from must never touch state, messages,
  // dialogs or `onUserUpdated`.
  const session = useRef(createModalSession());
  const [showTradevestRetryModal, setShowTradevestRetryModal] = useState(false);
  const [tradevestResetLoading, setTradevestResetLoading] = useState(false);
  const [showSetStatusModal, setShowSetStatusModal] = useState(false);
  const [setStatusLoading, setSetStatusLoading] = useState(false);
  const [setStatusForm] = Form.useForm();
  const [isOnline, setIsOnline] = useState<boolean | null>(null);
  const [socketCount, setSocketCount] = useState(0);
  const [pingLoading, setPingLoading] = useState(false);
  const [pingResult, setPingResult] = useState<DevicePingResponse | null>(null);
  const { staff } = useAuth();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const tradevestPortfolioEnabled = visible && !!userId && userDetail?.user.tradevestProvisioningStatus === 'completed';

  const tradevestPortfolioQuery = useQuery({
    queryKey: ['tradevestPortfolio', userId],
    queryFn: async () => {
      const response = await usersService.getTradevestPortfolio(userId!);
      return response.data ?? null;
    },
    enabled: tradevestPortfolioEnabled,
    staleTime: 30_000,
    retry: false,
  });

  const tradevestPortfolio = tradevestPortfolioEnabled ? (tradevestPortfolioQuery.data ?? null) : null;
  const tradevestPortfolioLoading = tradevestPortfolioEnabled && tradevestPortfolioQuery.isFetching;
  const tradevestPortfolioError = tradevestPortfolioQuery.error
    ? tradevestPortfolioQuery.error instanceof ApiError
      ? tradevestPortfolioQuery.error.message
      : 'Failed to load Tradevest portfolio'
    : null;

  useEffect(() => {
    // Runs first, before anything else: it retires every token captured by an in-flight continuation from the
    // previous customer (or the previous open), and clears the loading flags those continuations would otherwise
    // leave stuck forever once they return early.
    session.current.begin();
    setTradevestRetryLoading(false);
    setTradevestResetLoading(false);
    setSetStatusLoading(false);
    setEditDetailsLoading(false);
    // A retry result belongs to the customer it ran for; never leave it open over the next one.
    setShowTradevestRetryModal(false);
    setTradevestRetryResult(null);
    if (visible && userId) {
      openUserId.current = userId;
      guidanceRequests.current.setActive(userId);
      fetchUserDetails();
      fetchPresence();
      // Reopening this customer keeps the push history block mounted, so nothing else would reload it
      void queryClient.invalidateQueries({ queryKey: [PUSH_HISTORY_QUERY_KEY, userId] });
    } else {
      openUserId.current = null;
      setIsOnline(null);
      setSocketCount(0);
      setPingResult(null);
      // Closing drops any guidance request still in flight, so its answer cannot land on the next customer.
      guidanceRequests.current.invalidate();
      setTradevestGuidance(null);
    }
  }, [visible, userId]);

  const fetchTradevestGuidance = async () => {
    if (!userId) return;
    // Only the latest request may write: a late answer for the previous customer would otherwise show their card, or
    // put their Tradevest IDs into the copied request, while the next customer is open.
    const requestId = guidanceRequests.current.start(userId);
    if (requestId === null) return;
    const token = session.current.current();
    setTradevestGuidance(null);
    try {
      const response = await usersService.getTradevestGuidance(userId);
      if (!guidanceRequests.current.isCurrent(requestId) || !session.current.isCurrent(token)) return;
      setTradevestGuidance(response.success && response.data ? { userId, data: response.data } : null);
    } catch (error) {
      if (!guidanceRequests.current.isCurrent(requestId) || !session.current.isCurrent(token)) return;
      // An aid, not the source of truth: the status and the buttons below still work without it.
      console.error('Error fetching Tradevest guidance:', error);
      setTradevestGuidance(null);
    }
  };

  const handleCopyTradevestRequest = async () => {
    const token = session.current.current();
    // Refuse silently (no copy, no message) when the guidance on hand was fetched for a different customer than
    // the one currently displayed — a late answer for the previous customer must never be copied for the next one.
    if (!userId || !tradevestGuidance || tradevestGuidance.userId !== userId || userDetail?.user.id !== userId) return;
    const text = tradevestGuidance.data.tradevestRequestText;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      if (!session.current.isCurrent(token)) return;
      message.success('Anfrage kopiert');
    } catch {
      if (!session.current.isCurrent(token)) return;
      message.error('Kopieren fehlgeschlagen');
    }
  };

  const handleRemindCustomer = (template: TradevestReminderTemplate) => {
    setReminderTemplate(template);
    setShowNotificationModal(true);
  };

  const fetchUserDetails = async () => {
    if (!userId) return;
    // Stops a stale mutation continuation (Retry, Reset, Set Status, edit details) from refreshing the wrong
    // customer if staff closed this one and opened another before the request finished.
    if (userId !== openUserId.current) return;
    const token = session.current.current();
    void fetchTradevestGuidance();
    setLoading(true);
    // Reset account/card state so a previously opened user's PayCenter card
    // doesn't leak into a user without PayCenter (fetchUserAccounts is conditional below).
    setAccountsData(null);
    setAccountsError(null);
    try {
      const response = await usersService.getUserDetails(userId);
      // A late answer for the previous customer must never be shown: bail out before touching any state.
      if (!session.current.isCurrent(token)) return;
      if (response.data) {
        setUserDetail(response.data);
        if (response.data.user.paycenterCustomerId) {
          fetchUserAccounts();
        }
      }
    } catch (error) {
      if (!session.current.isCurrent(token)) return;
      message.error('Failed to fetch user details');
      console.error('Error fetching user details:', error);
    } finally {
      if (session.current.isCurrent(token)) setLoading(false);
    }
  };

  const fetchPresence = async () => {
    if (!userId) return;
    try {
      const response = await usersService.getUserPresence(userId);
      if (response.data) {
        setIsOnline(response.data.isOnline);
        setSocketCount(response.data.socketCount);
      }
    } catch {
      setIsOnline(null);
    }
  };

  const handlePing = async () => {
    if (!userId) return;
    setPingLoading(true);
    setPingResult(null);
    try {
      const response = await usersService.pingUserDevice(userId);
      if (response.data) {
        setPingResult(response.data);
        if (response.data.pinged) {
          message.success(`Device responded in ${response.data.latencyMs}ms`);
        } else {
          message.warning('Device did not respond to ping');
        }
      }
    } catch {
      message.error('Failed to ping device');
    } finally {
      setPingLoading(false);
    }
  };

  const fetchUserAccounts = async () => {
    if (!userId) return;
    const token = session.current.current();
    setAccountsLoading(true);
    setAccountsError(null);
    try {
      const response = await usersService.getUserAccounts(userId);
      if (!session.current.isCurrent(token)) return;
      if (response.success && response.data) {
        setAccountsData(response.data);
      }
    } catch (error) {
      if (!session.current.isCurrent(token)) return;
      console.error('Error fetching user accounts:', error);
      setAccountsError('Unable to fetch balance');
    } finally {
      if (session.current.isCurrent(token)) setAccountsLoading(false);
    }
  };

  const openWalletBalances = () => {
    if (userId) window.open(`/wallet-balances/${userId}`, '_blank');
  };
  const openTradevestLog = () => {
    if (userId) window.open(`/tradevest-log/${userId}`, '_blank');
  };

  const handleTradevestRetry = async () => {
    if (!userId) return;
    const token = session.current.current();
    setTradevestRetryLoading(true);
    setTradevestRetryResult(null);
    setShowTradevestRetryModal(true);
    try {
      const response = await usersService.retryTradevestOnboarding(userId);
      if (!session.current.isCurrent(token)) return;
      if (response.success && response.data) {
        setTradevestRetryResult(response.data);
        fetchUserDetails();
      } else {
        message.error(response.error?.message || 'Retry failed');
        setShowTradevestRetryModal(false);
      }
    } catch (error) {
      if (!session.current.isCurrent(token)) return;
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to retry onboarding';
      message.error(errorMessage);
      setShowTradevestRetryModal(false);
    } finally {
      if (session.current.isCurrent(token)) setTradevestRetryLoading(false);
    }
  };

  const handleTradevestReset = async () => {
    if (!userId) return;
    const token = session.current.current();
    setTradevestResetLoading(true);
    try {
      const response = await usersService.resetTradevestOnboarding(userId);
      if (!session.current.isCurrent(token)) return;
      if (response.success && response.data) {
        message.success(`${response.data.message} (${response.data.clearedFields.length} fields cleared)`);
        fetchUserDetails();
        onUserUpdated?.();
      } else {
        message.error(response.error?.message || 'Reset failed');
      }
    } catch (error) {
      if (!session.current.isCurrent(token)) return;
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to reset onboarding';
      message.error(errorMessage);
    } finally {
      if (session.current.isCurrent(token)) setTradevestResetLoading(false);
    }
  };

  const handleSetTradevestStatus = async () => {
    if (!userId) return;
    const token = session.current.current();
    try {
      const values = await setStatusForm.validateFields();
      if (!session.current.isCurrent(token)) return;
      setSetStatusLoading(true);
      const response = await usersService.setTradevestProvisioningStatus(
        userId,
        values.status,
        values.reason,
        values.clearError ?? false,
      );
      if (!session.current.isCurrent(token)) return;
      if (response.success && response.data) {
        message.success(response.data.message);
        setShowSetStatusModal(false);
        setStatusForm.resetFields();
        fetchUserDetails();
        onUserUpdated?.();
      } else {
        message.error(response.error?.message || 'Failed to set status');
      }
    } catch (error) {
      if (!session.current.isCurrent(token)) return;
      if (error instanceof ApiError) message.error(error.message);
    } finally {
      if (session.current.isCurrent(token)) setSetStatusLoading(false);
    }
  };

  const renderStatusOptGroup = (label: string, statuses: readonly string[]) => (
    <Select.OptGroup label={label}>
      {statuses.map(s => (
        <Select.Option key={s} value={s} label={`${s} ${STATUS_DESCRIPTIONS[s] || ''}`}>
          <span>
            {s}
            {s === userDetail?.user.tradevestProvisioningStatus && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                current
              </Tag>
            )}
          </span>
          <div style={{ fontSize: 11, color: '#888', lineHeight: 1.3 }}>{STATUS_DESCRIPTIONS[s]}</div>
        </Select.Option>
      ))}
    </Select.OptGroup>
  );

  const openSetStatusModal = () => {
    const currentStatus = userDetail?.user.tradevestProvisioningStatus || 'not_started';
    setStatusForm.setFieldsValue({
      status: currentStatus,
      reason: '',
      clearError: !!userDetail?.user.tradevestProvisioningError,
    });
    setShowSetStatusModal(true);
  };

  const handleSuspendUser = async () => {
    if (!userId || !suspendReason.trim()) {
      message.error('Please provide a reason for suspension');
      return;
    }
    setActionLoading(true);
    try {
      await usersService.suspendUser(userId, suspendReason);
      message.success('User suspended successfully');
      fetchUserDetails();
      onUserUpdated?.();
      setSuspendReason('');
    } catch (error) {
      message.error('Failed to suspend user');
      console.error('Error suspending user:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateUser = async () => {
    if (!userId) return;
    setActionLoading(true);
    try {
      await usersService.activateUser(userId);
      message.success('User activated successfully');
      fetchUserDetails();
      onUserUpdated?.();
    } catch (error) {
      message.error('Failed to activate user');
      console.error('Error activating user:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPendingVerification = async () => {
    if (!userId) return;
    setActionLoading(true);
    try {
      await usersService.setPendingVerification(userId);
      message.success('User status set to pending verification successfully');
      fetchUserDetails();
      onUserUpdated?.();
    } catch (error) {
      message.error('Failed to set user status to pending verification');
      console.error('Error setting user to pending verification:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPin = async () => {
    if (!userId) return;
    setActionLoading(true);
    try {
      await usersService.resetUserPin(userId);
      message.success('PIN reset successfully');
      fetchUserDetails();
    } catch (error) {
      message.error('Failed to reset PIN');
      console.error('Error resetting PIN:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleProvisionCrypto = async () => {
    if (!userId) return;
    setActionLoading(true);
    try {
      const response = await usersService.provisionCrypto(userId);
      message.success(response.data?.message || 'Crypto provisioning triggered successfully');
      fetchUserDetails();
      onUserUpdated?.();
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to trigger crypto provisioning';
      message.error(errorMessage);
      console.error('Error provisioning crypto:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivatePayCenter = async () => {
    if (!userId) return;
    setDeactivateLoading(true);
    try {
      const response = await usersService.deactivatePayCenterAccount(userId);
      message.success(response.data?.message || 'PayCenter account deactivation initiated successfully');
      fetchUserDetails();
      onUserUpdated?.();
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to deactivate PayCenter account';
      message.error(errorMessage);
      console.error('Error deactivating PayCenter account:', error);
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleRegisterPayCenter = async () => {
    if (!userId) return;
    setRegisterPayCenterLoading(true);
    try {
      const response = await usersService.registerPayCenter(userId);
      message.success(response.data?.message || 'PayCenter registration triggered successfully');
      fetchUserDetails();
      onUserUpdated?.();
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.message : 'Failed to trigger PayCenter registration';
      message.error(errorMessage);
      console.error('Error triggering PayCenter registration:', error);
    } finally {
      setRegisterPayCenterLoading(false);
    }
  };

  const isAdmin = staff?.role === 'ADMIN';
  const isAdminOrSupport = staff?.role === 'ADMIN' || staff?.role === 'SUPPORT';

  const getGrafanaLogsUrl = (uid: string) => {
    const baseUrl = 'https://grafana.mygateapp.de/explore';
    const query = {
      schemaVersion: 1,
      panes: {
        d4t: {
          datasource: 'P8E80F9AEF21F6940',
          queries: [
            {
              refId: 'A',
              expr: `{compose_service="backend"} |= \`${uid}\``,
              queryType: 'range',
              datasource: { type: 'loki', uid: 'P8E80F9AEF21F6940' },
              editorMode: 'builder',
              direction: 'forward',
            },
          ],
          range: { from: 'now-1h', to: 'now' },
        },
      },
    };
    const queryString = `?schemaVersion=1&panes=${encodeURIComponent(JSON.stringify(query.panes))}&orgId=1`;
    return `${baseUrl}${queryString}`;
  };

  const openGrafanaLogs = () => {
    if (userDetail?.user.id) window.open(getGrafanaLogsUrl(userDetail.user.id), '_blank');
  };

  const getPosthogUrl = (uid: string) => {
    const query = {
      kind: 'DataTableNode',
      source: {
        kind: 'ActorsQuery',
        select: ['person_display_name -- Person ', 'id', 'created_at', 'person.$delete'],
        search: uid,
      },
      full: true,
      propertiesViaUrl: true,
    };
    return `https://eu.posthog.com/project/78740/persons#q=${encodeURIComponent(JSON.stringify(query))}`;
  };

  const openPosthog = () => {
    if (userDetail?.user.id) window.open(getPosthogUrl(userDetail.user.id), '_blank');
  };

  const fetchCardProperties = async () => {
    if (!userId || !userDetail?.user.paycenterCustomerId) {
      message.warning('User does not have a PayCenter account');
      return;
    }
    setCardPropertiesLoading(true);
    try {
      const [validationResponse, satisfiedResponse] = await Promise.all([
        usersService.getCardPropertyValidation(userId),
        usersService.getPlasticCardSatisfied(userId),
      ]);
      setCardValidationData(validationResponse.data);
      setPlasticCardSatisfiedData(satisfiedResponse.data);
      setShowCardPropertiesModal(true);
    } catch (error) {
      const apiError = error as ApiError;
      message.error(apiError.message || 'Failed to fetch card properties');
      console.error('Error fetching card properties:', error);
    } finally {
      setCardPropertiesLoading(false);
    }
  };

  const openKycCases = () => {
    if (userId) window.open(`/kyc-cases/${userId}`, '_blank');
  };

  const handleOpenEditDetails = () => {
    if (userDetail) {
      editDetailsForm.setFieldsValue({
        firstName: userDetail.user.firstName || '',
        lastName: userDetail.user.lastName || '',
        title: userDetail.user.title || undefined,
        taxId: userDetail.user.taxId || '',
      });
      setShowEditDetailsModal(true);
    }
  };

  const handleEditDetailsSubmit = async () => {
    const token = session.current.current();
    try {
      const values = await editDetailsForm.validateFields();
      if (!session.current.isCurrent(token)) return;
      setEditDetailsLoading(true);
      const response = await usersService.updateUserDetails(userId!, values);
      if (!session.current.isCurrent(token)) return;
      if (response.success) {
        message.success('User details updated successfully');
        setShowEditDetailsModal(false);
        fetchUserDetails();
        onUserUpdated?.();
      } else {
        message.error(response.error?.message || 'Failed to update user details');
      }
    } catch (error) {
      if (!session.current.isCurrent(token)) return;
      message.error('Failed to update user details');
      console.error('Error updating user details:', error);
    } finally {
      if (session.current.isCurrent(token)) setEditDetailsLoading(false);
    }
  };

  const handleOpenEditEmail = () => {
    editEmailForm.resetFields();
    setShowEditEmailModal(true);
  };

  const handleEditEmailSubmit = async () => {
    try {
      const values = await editEmailForm.validateFields();
      setEditEmailLoading(true);
      const response = await usersService.updateUserEmail(userId!, {
        email: values.email.trim(),
        reason: values.reason.trim(),
      });
      const stale = response.data?.staleAtProviders ?? [];
      const verification = response.data?.verification;
      // Naming the providers here is the whole point: nothing propagates the change for us,
      // and the operator is the only one in a position to follow it up.
      const staleNote =
        stale.length > 0 ? ` Still on the old address at: ${stale.map(entry => entry.provider).join(', ')}` : '';
      // What happened to the verified flag is not the operator's choice — it follows from whether
      // the previous address was ever proven — so it has to be reported back, or the operator
      // closes a ticket believing the address is usable when it still needs a code.
      const outcome = {
        CONFIRMED_BY_STAFF: 'Email updated and marked verified.',
        CODE_SENT: 'Email updated. The previous address was never verified, so a code was sent to the new one.',
        CODE_SEND_FAILED: 'Email updated, but the verification code could not be sent — ask the user to resend.',
        UNCHANGED: 'Address was already on file and verified; nothing changed.',
      }[verification ?? 'CONFIRMED_BY_STAFF'];

      if (verification === 'CODE_SEND_FAILED') {
        message.warning(`${outcome}${staleNote}`);
      } else {
        message.success(`${outcome}${staleNote}`);
      }
      setShowEditEmailModal(false);
      fetchUserDetails();
      onUserUpdated?.();
    } catch (error) {
      // validateFields rejects with the field errors; those are already rendered inline.
      if ((error as { errorFields?: unknown[] })?.errorFields) return;
      // `api.request` throws on every non-2xx, so this is where the backend's message lands —
      // including the 409 that names the account still holding the address, which is the whole
      // reason the endpoint builds it.
      message.error(error instanceof ApiError ? error.message : 'Failed to update email');
      console.error('Error updating user email:', error);
    } finally {
      setEditEmailLoading(false);
    }
  };

  // ─── derived render data ───────────────────────────────────────────────
  const u = userDetail?.user;
  // MYG-769: Tradevest rejects natural-person creation for any residency but DE without a tax id.
  const taxIdRequired = !!u?.country && requiresTaxId(u.country);
  const fullName = userDetail
    ? formatFullName(userDetail.user.firstName, userDetail.user.lastName, userDetail.user.title)
    : 'N/A';
  const initials = u ? getInitials(u.firstName, u.lastName) : '?';
  const statusPill = u ? getStatusPill(u.status) : null;
  const kycPill = u ? getKycPill(userDetail!.kycCase) : null;
  // Only render/use the guidance when it was fetched for the customer currently on screen — a late answer for
  // the previous customer must never drive the card, the Retry button tone, or the reset guard below.
  const storedGuidance =
    tradevestGuidance && userId && u && tradevestGuidance.userId === userId && tradevestGuidance.userId === u.id
      ? tradevestGuidance.data
      : null;
  // A guidance fetch failure must not silently re-enable Reset for tax_id_required — only a tax number
  // unblocks that status (see executeRetryForStatus), never a retry or a reset.
  const tradevestResetForbidden =
    storedGuidance?.guidance?.resetForbidden === true || u?.tradevestProvisioningStatus === 'tax_id_required';
  const planLabel = u?.paycenterDeactivated
    ? 'PayCenter deactivated'
    : u?.needsLegacyPlanSelection
      ? 'Plan required'
      : u?.paycenterPlan || null;
  const planTone: Tone = u?.paycenterDeactivated ? 'red' : u?.needsLegacyPlanSelection ? 'amber' : 'blue';
  const account = accountsData && accountsData.length > 0 ? accountsData[0] : null;

  return (
    <Modal
      className="user-detail-modal"
      open={visible}
      onCancel={onClose}
      width={1280}
      style={{ top: 20 }}
      footer={null}
      closable={false}
      styles={{
        body: {
          padding: 0,
          background: '#f7f8fa',
          maxHeight: 'calc(100vh - 80px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
        content: { padding: 0, overflow: 'hidden' },
      }}
    >
      <div className="udm-root" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {loading && !userDetail ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" />
          </div>
        ) : userDetail && u && statusPill && kycPill ? (
          <>
            {/* ── HEADER ── */}
            <header
              className="user-detail-header"
              style={{
                padding: '18px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                borderBottom: '1px solid var(--udm-border)',
                background: 'linear-gradient(180deg, #ffffff, #fbfcfd)',
                flexShrink: 0,
              }}
            >
              <div
                className="user-detail-avatar"
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 600,
                  fontSize: 18,
                  letterSpacing: 0.5,
                  flexShrink: 0,
                  boxShadow: '0 4px 10px -4px rgba(15,18,23,0.4)',
                }}
              >
                {initials}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: -0.2 }}>{fullName}</h2>
                  <button
                    title="Edit name"
                    type="button"
                    onClick={handleOpenEditDetails}
                    className="udm-iconbtn"
                    style={{
                      background: 'transparent',
                      border: '1px solid transparent',
                      color: 'var(--udm-text-3)',
                      borderRadius: 6,
                      padding: 4,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <Icon.edit />
                  </button>
                  <span style={{ width: 1, height: 16, background: 'var(--udm-border-strong)' }} />
                  <Pill tone={statusPill.tone} icon={statusPill.icon}>
                    {statusPill.label}
                  </Pill>
                  <Pill tone={u.hasPinSet ? 'green' : 'amber'}>{u.hasPinSet ? 'PIN set' : 'No PIN'}</Pill>
                  <Pill tone={kycPill.tone}>{kycPill.label}</Pill>
                  {planLabel && <Pill tone={planTone}>{planLabel}</Pill>}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    color: 'var(--udm-text-3)',
                    fontSize: 12.5,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontFamily: 'var(--udm-mono)' }}>{u.id}</span>
                  <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--udm-text-3)' }} />
                  <span>Created {formatDateTime(u.createdAt)}</span>
                  {u.lastLoginAt && (
                    <>
                      <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--udm-text-3)' }} />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {isOnline && (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: 'var(--udm-green)',
                              boxShadow: '0 0 0 2px rgba(21,128,61,0.15)',
                            }}
                          />
                        )}
                        Last login {formatDateTime(u.lastLoginAt)}
                      </span>
                    </>
                  )}
                  {u.phoneNumber && (
                    <>
                      <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--udm-text-3)' }} />
                      <span style={{ fontFamily: 'var(--udm-mono)' }}>{u.phoneNumber}</span>
                    </>
                  )}
                </div>
              </div>

              <div className="user-detail-header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isAdminOrSupport && (
                  <Btn icon={<Icon.bell />} size="sm" onClick={() => setShowNotificationModal(true)}>
                    Notify
                  </Btn>
                )}
                <Btn icon={<Icon.log />} size="sm" onClick={openGrafanaLogs}>
                  Logs
                </Btn>
                <button
                  onClick={onClose}
                  type="button"
                  title="Close"
                  className="udm-iconbtn"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--udm-border)',
                    color: 'var(--udm-text-2)',
                    borderRadius: 8,
                    padding: 7,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <Icon.close />
                </button>
              </div>
            </header>

            {/* ── BODY (scrollable) ── */}
            <div className="user-detail-body" style={{ overflow: 'auto', flex: 1, padding: 20 }}>
              {/* banners */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {u.paycenterDeactivated && (
                  <Alert
                    type="error"
                    showIcon
                    message="PayCenter account deactivated"
                    description={`Deactivated on ${formatDateTime(u.paycenterDeactivatedAt)}`}
                  />
                )}
                {u.needsLegacyPlanSelection && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Plan selection required"
                    description="Legacy user needs to select a subscription plan (SMART) or request account deletion."
                  />
                )}
                {u.paycenterProvisioningStatus === 'failed' && (
                  <Alert
                    type="error"
                    showIcon
                    message="PayCenter provisioning failed"
                    description={u.paycenterProvisioningError || 'No error message available.'}
                  />
                )}
                {u.unsatisfiedConstraints && u.unsatisfiedConstraints.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Unsatisfied constraints"
                    description={
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                        {u.unsatisfiedConstraints.map(c => (
                          <Pill key={c} tone="amber">
                            {c}
                          </Pill>
                        ))}
                      </div>
                    }
                  />
                )}
              </div>

              {/* card grid */}
              <div className="udm-grid">
                {/* IDENTITY & CONTACT */}
                <div style={{ gridColumn: 'span 4' }}>
                  <SectionCard title="Identity & Contact" icon={<Icon.shield />}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 14 }}>
                      <Field label="Full Name" span={2}>
                        <span style={{ fontWeight: 500 }}>{fullName}</span>
                        <button
                          type="button"
                          title="Edit"
                          onClick={handleOpenEditDetails}
                          className="udm-iconbtn"
                          style={EDIT_ICON_BUTTON_STYLE}
                        >
                          <Icon.edit />
                        </button>
                      </Field>
                      <Field label="Phone">
                        <span style={{ fontFamily: 'var(--udm-mono)', fontSize: 12.5 }}>{u.phoneNumber || '—'}</span>
                      </Field>
                      <Field label="Language">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Icon.globe />
                          <span>{u.preferredLanguage || 'en'}</span>
                        </span>
                      </Field>
                      <Field label="Email" span={2}>
                        {u.email ? (
                          <>
                            <CopyField value={u.email} mono={false} />
                            {u.isEmailConfirmed && (
                              <Pill tone="green" icon={<Icon.check />}>
                                Verified
                              </Pill>
                            )}
                          </>
                        ) : (
                          <span style={{ color: 'var(--udm-text-3)' }}>—</span>
                        )}
                        <button
                          type="button"
                          title="Change email address"
                          onClick={handleOpenEditEmail}
                          className="udm-iconbtn"
                          style={EDIT_ICON_BUTTON_STYLE}
                        >
                          <Icon.edit />
                        </button>
                      </Field>
                      <Field label="User ID" span={2}>
                        <CopyField value={u.id} />
                      </Field>
                    </div>
                  </SectionCard>
                </div>

                {/* ACCOUNT STATUS */}
                <div style={{ gridColumn: 'span 4' }}>
                  <SectionCard title="Account Status" icon={<Icon.bank />}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                      <StatusTile label="Status" value={statusPill.label} tone={statusPill.tone} />
                      <StatusTile label="KYC" value={kycPill.label.replace(/^KYC /, '')} tone={kycPill.tone} />
                      <StatusTile
                        label="PIN"
                        value={u.hasPinSet ? 'Set' : 'Not set'}
                        tone={u.hasPinSet ? 'green' : 'amber'}
                      />
                      <StatusTile
                        label={`${socketCount || 0} ${socketCount === 1 ? 'device' : 'devices'}`}
                        value={isOnline === null ? 'Unknown' : isOnline ? 'Online' : 'Offline'}
                        tone={isOnline ? 'green' : 'neutral'}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <Field label="PayCenter Plan">
                        {planLabel ? (
                          <Pill tone={planTone}>{planLabel}</Pill>
                        ) : (
                          <span style={{ color: 'var(--udm-text-3)' }}>Not selected</span>
                        )}
                      </Field>

                      <Field label="PayCenter Customer ID">
                        {u.paycenterCustomerId ? (
                          <>
                            <CopyField value={u.paycenterCustomerId} />
                            {account?.iban && (
                              <>
                                <span style={{ color: 'var(--udm-text-3)' }}>•</span>
                                <CopyField value={account.iban} />
                              </>
                            )}
                          </>
                        ) : (
                          <span style={{ color: 'var(--udm-text-3)' }}>Not assigned</span>
                        )}
                      </Field>

                      {u.paycenterCustomerId && (
                        <Field label="Balance" align="baseline">
                          {accountsLoading ? (
                            <Spin size="small" />
                          ) : accountsError ? (
                            <span style={{ color: 'var(--udm-text-3)', fontSize: 12 }}>{accountsError}</span>
                          ) : account ? (
                            <>
                              <span style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.2 }}>
                                {new Intl.NumberFormat('de-DE', {
                                  style: 'currency',
                                  currency: account.currency || 'EUR',
                                }).format(account.balance)}
                              </span>
                              <span style={{ color: 'var(--udm-text-3)', fontSize: 12 }}>available</span>
                            </>
                          ) : (
                            <span style={{ color: 'var(--udm-text-3)' }}>—</span>
                          )}
                        </Field>
                      )}

                      {account && <PayCenterCardRow account={account} />}

                      {(u.paycenterProvisioningStartedAt || u.paycenterProvisioningCompletedAt) && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            paddingTop: 6,
                            borderTop: '1px dashed var(--udm-border)',
                          }}
                        >
                          <span style={{ fontSize: 11.5, color: 'var(--udm-text-3)' }}>
                            Provisioning started: {formatDateTime(u.paycenterProvisioningStartedAt)}
                          </span>
                          <span style={{ fontSize: 11.5, color: 'var(--udm-text-3)' }}>
                            Provisioning completed: {formatDateTime(u.paycenterProvisioningCompletedAt)}
                          </span>
                        </div>
                      )}
                    </div>
                  </SectionCard>
                </div>

                {/* ACTIVITY & DEVICES */}
                <div style={{ gridColumn: 'span 4' }}>
                  <SectionCard title="Activity & Devices" icon={<Icon.device />}>
                    <Field label="Last Login">
                      {u.lastLoginAt ? (
                        <>
                          <span style={{ fontWeight: 500 }}>{formatDateTime(u.lastLoginAt)}</span>
                          <span style={{ color: 'var(--udm-text-3)', fontSize: 12 }}>
                            {formatRelative(u.lastLoginAt)}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: 'var(--udm-text-3)' }}>Never</span>
                      )}
                    </Field>
                    <Field label="Created">
                      <span>{formatDateTime(u.createdAt)}</span>
                      <span style={{ color: 'var(--udm-text-3)', fontSize: 12 }}>{formatRelative(u.createdAt)}</span>
                    </Field>

                    <Field label="Devices">
                      {isOnline === null ? (
                        <Pill tone="neutral">Unknown</Pill>
                      ) : isOnline ? (
                        <Pill
                          tone="green"
                          icon={
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: 'var(--udm-green)',
                              }}
                            />
                          }
                        >
                          Online · {socketCount} {socketCount === 1 ? 'device' : 'devices'}
                        </Pill>
                      ) : (
                        <Pill tone="neutral" icon={<Icon.alert />}>
                          Offline
                        </Pill>
                      )}
                      <Btn
                        icon={<Icon.plug />}
                        size="sm"
                        onClick={handlePing}
                        loading={pingLoading}
                        disabled={!isOnline}
                      >
                        Ping
                      </Btn>
                    </Field>

                    {pingResult && (
                      <div
                        style={{
                          fontSize: 12,
                          color: pingResult.pinged ? 'var(--udm-green)' : 'var(--udm-text-3)',
                          fontFamily: 'var(--udm-mono)',
                        }}
                      >
                        {pingResult.pinged
                          ? `${pingResult.latencyMs}ms · ${pingResult.respondedCount}/${pingResult.totalSockets}`
                          : 'No response'}
                      </div>
                    )}
                  </SectionCard>
                </div>

                {/* TRADEVEST */}
                <div style={{ gridColumn: 'span 7' }}>
                  <SectionCard title="Tradevest" icon={<Icon.chart />}>
                    {u.tradevestProvisioningStatus === 'completed' ? (
                      <TradevestHero
                        portfolio={tradevestPortfolio}
                        loading={tradevestPortfolioLoading}
                        error={tradevestPortfolioError}
                      />
                    ) : (
                      <div
                        style={{
                          padding: 14,
                          background: 'linear-gradient(180deg, #fafbfc, #f4f6f9)',
                          border: '1px solid var(--udm-border)',
                          borderRadius: 10,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 11,
                            color: 'var(--udm-text-3)',
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            fontWeight: 600,
                          }}
                        >
                          Status
                        </span>
                        {u.tradevestProvisioningStatus ? (
                          <Pill
                            tone={
                              u.tradevestProvisioningStatus.includes('failed') ||
                              u.tradevestProvisioningStatus.includes('rejected') ||
                              u.tradevestProvisioningStatus.includes('timeout') ||
                              u.tradevestProvisioningStatus.includes('error') ||
                              u.tradevestProvisioningStatus === 'unknown_error'
                                ? 'red'
                                : 'blue'
                            }
                          >
                            {u.tradevestProvisioningStatus}
                          </Pill>
                        ) : (
                          <Pill tone="neutral">Not started</Pill>
                        )}
                      </div>
                    )}

                    {storedGuidance && (
                      <TradevestGuidanceBlock
                        response={storedGuidance}
                        isAdmin={isAdmin}
                        onRemind={handleRemindCustomer}
                        onCopyRequest={() => void handleCopyTradevestRequest()}
                      />
                    )}

                    {u.tradevestProvisioningError && (
                      <div
                        style={{
                          background: 'var(--udm-red-tint)',
                          border: '1px solid var(--udm-red-border)',
                          color: 'var(--udm-red)',
                          padding: '8px 10px',
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      >
                        {u.tradevestProvisioningError}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <Field label="Customer">
                        {u.tradevestCustomerId ? (
                          <CopyField value={u.tradevestCustomerId} truncate />
                        ) : (
                          <span style={{ color: 'var(--udm-text-3)' }}>—</span>
                        )}
                      </Field>
                      <Field label="IBAN">
                        {u.tradevestIban ? (
                          <CopyField value={u.tradevestIban} />
                        ) : (
                          <span style={{ color: 'var(--udm-text-3)' }}>—</span>
                        )}
                      </Field>
                      {/* MYG-769: a missing tax id is what blocks onboarding for a non-DE resident. */}
                      <Field label={`Tax ID${u.country ? ` (${u.country})` : ''}`}>
                        {u.taxId ? (
                          <CopyField value={u.taxId} />
                        ) : (
                          <span style={{ color: taxIdRequired ? 'var(--udm-red)' : 'var(--udm-text-3)' }}>
                            {taxIdRequired ? 'Missing — required for this tax residency' : '—'}
                          </span>
                        )}
                      </Field>
                    </div>

                    {(u.tradevestProvisioningStartedAt || u.tradevestProvisioningCompletedAt) && (
                      <div
                        style={{
                          display: 'flex',
                          gap: 16,
                          fontSize: 11.5,
                          color: 'var(--udm-text-3)',
                        }}
                      >
                        {u.tradevestProvisioningStartedAt && (
                          <span>Started: {formatDateTime(u.tradevestProvisioningStartedAt)}</span>
                        )}
                        {u.tradevestProvisioningCompletedAt && (
                          <span>Completed: {formatDateTime(u.tradevestProvisioningCompletedAt)}</span>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'auto' }}>
                      <Btn tone="primary" icon={<Icon.log />} size="sm" onClick={openTradevestLog}>
                        View Onboarding Log
                      </Btn>
                      {u.tradevestProvisioningStatus === 'completed' && (
                        <Btn icon={<Icon.chart />} size="sm" onClick={() => setShowTradevestTaxModal(true)}>
                          Steuern & Kapitalmaßnahmen
                        </Btn>
                      )}
                      {isAdmin &&
                        u.tradevestProvisioningStatus &&
                        u.tradevestProvisioningStatus !== 'completed' &&
                        u.tradevestProvisioningStatus !== 'not_started' && (
                          <Btn
                            tone={storedGuidance?.guidance?.action === 'ADMIN_RETRY' ? 'primary' : 'neutral'}
                            icon={<Icon.refresh />}
                            size="sm"
                            loading={tradevestRetryLoading}
                            onClick={handleTradevestRetry}
                          >
                            Retry Onboarding
                          </Btn>
                        )}
                      {isAdmin && u.tradevestProvisioningStatus && u.tradevestProvisioningStatus !== 'not_started' && (
                        <Popconfirm
                          title="Reset Tradevest Onboarding"
                          description="This will clear ALL Tradevest data. The user must restart from the mobile app. This cannot be undone."
                          onConfirm={handleTradevestReset}
                          okButtonProps={{ danger: true, loading: tradevestResetLoading }}
                          okText="Reset"
                          cancelText="Cancel"
                          disabled={tradevestResetForbidden}
                        >
                          <span
                            title={
                              tradevestResetForbidden
                                ? 'Nicht zurücksetzen: Steuer-ID eintragen, dann läuft das Onboarding weiter'
                                : undefined
                            }
                          >
                            <Btn
                              tone="danger"
                              icon={<Icon.refresh />}
                              size="sm"
                              loading={tradevestResetLoading}
                              disabled={tradevestResetForbidden}
                            >
                              Reset Onboarding
                            </Btn>
                          </span>
                        </Popconfirm>
                      )}
                      {isAdmin && u.tradevestProvisioningStatus && u.tradevestProvisioningStatus !== 'not_started' && (
                        <Btn icon={<Icon.edit />} size="sm" onClick={openSetStatusModal}>
                          Set Status
                        </Btn>
                      )}
                    </div>
                  </SectionCard>
                </div>

                {/* CRYPTO WALLETS */}
                <div style={{ gridColumn: 'span 5' }}>
                  <SectionCard title="Crypto Wallets" icon={<Icon.wallet />}>
                    {u.walletAddresses && u.walletAddresses.length > 0 ? (
                      <>
                        {u.walletAddresses.map(wallet => (
                          <div key={wallet.walletId} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <Field label="Wallet ID">
                              <CopyField value={wallet.walletId} />
                            </Field>
                            <div
                              style={{
                                border: '1px solid var(--udm-border)',
                                borderRadius: 10,
                                background: 'var(--udm-panel-2)',
                                overflow: 'hidden',
                              }}
                            >
                              {wallet.btcAddress && (
                                <>
                                  <WalletRow chain="BTC" color="#f7931a" addr={wallet.btcAddress} />
                                  {wallet.evmAddress && <div style={{ height: 1, background: 'var(--udm-border)' }} />}
                                </>
                              )}
                              {wallet.evmAddress && <WalletRow chain="EVM" color="#627eea" addr={wallet.evmAddress} />}
                              {!wallet.btcAddress && !wallet.evmAddress && (
                                <div style={{ padding: '10px 12px', color: 'var(--udm-text-3)', fontSize: 12 }}>
                                  Addresses pending…
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        <Btn tone="primary" icon={<Icon.wallet />} size="sm" onClick={openWalletBalances}>
                          View Wallet Balances
                        </Btn>
                      </>
                    ) : u.tanganyWallets && u.tanganyWallets.length > 0 ? (
                      <>
                        <Field label="Wallet IDs (addresses pending)">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {u.tanganyWallets.map(w => (
                              <CopyField key={w} value={w} />
                            ))}
                          </div>
                        </Field>
                        <Btn tone="primary" icon={<Icon.wallet />} size="sm" onClick={openWalletBalances}>
                          View Wallet Balances
                        </Btn>
                      </>
                    ) : (
                      <span style={{ color: 'var(--udm-text-3)' }}>No wallets</span>
                    )}
                  </SectionCard>
                </div>

                {/* PUSH HISTORY */}
                <div style={{ gridColumn: 'span 12' }}>
                  <SectionCard title="Push history" icon={<Icon.bell />}>
                    {/* Bound to the customer on screen, which stays the previous one while the next one loads */}
                    <PushHistoryBlock key={u.id} userId={u.id} />
                  </SectionCard>
                </div>
              </div>
            </div>

            {/* ── FOOTER ── */}
            <footer
              style={{
                borderTop: '1px solid var(--udm-border)',
                background: 'var(--udm-panel)',
                padding: '12px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--udm-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  fontWeight: 600,
                  marginRight: 4,
                }}
              >
                Account
              </span>
              <Btn icon={<Icon.shield />} size="sm" onClick={openKycCases}>
                View KYC Cases
              </Btn>
              {u.paycenterCustomerId && (
                <Btn icon={<Icon.card />} size="sm" onClick={fetchCardProperties} loading={cardPropertiesLoading}>
                  Card Properties
                </Btn>
              )}
              {isAdmin && u.status !== UserStatus.PENDING_VERIFICATION && (
                <Popconfirm
                  title="Are you sure you want to set this user's status to pending verification?"
                  onConfirm={handleSetPendingVerification}
                  okText="Yes"
                  cancelText="No"
                >
                  <span>
                    <Btn icon={<Icon.alert />} size="sm" loading={actionLoading}>
                      Set Pending Verification
                    </Btn>
                  </span>
                </Popconfirm>
              )}
              <Popconfirm
                title="Reset PIN?"
                description="The user will need to set a new PIN on their next login."
                onConfirm={handleResetPin}
                okText="Yes"
                cancelText="No"
              >
                <span>
                  <Btn icon={<Icon.refresh />} size="sm" loading={actionLoading}>
                    Reset PIN
                  </Btn>
                </span>
              </Popconfirm>
              {isAdmin &&
                userDetail.kycCase?.status === 'CLOSED' &&
                userDetail.kycCase?.result === 'SUCCESS' &&
                (!u.walletAddresses || u.walletAddresses.length === 0) && (
                  <Popconfirm
                    title="Provision Crypto Wallets"
                    description="This will create crypto wallets for the user."
                    onConfirm={handleProvisionCrypto}
                    okText="Yes, Provision"
                    cancelText="Cancel"
                  >
                    <span>
                      <Btn tone="primary" icon={<Icon.wallet />} size="sm" loading={actionLoading}>
                        Provision Crypto
                      </Btn>
                    </span>
                  </Popconfirm>
                )}
              {isAdmin &&
                !u.paycenterCustomerId &&
                (!u.paycenterProvisioningStatus ||
                  u.paycenterProvisioningStatus === 'not_started' ||
                  u.paycenterProvisioningStatus === 'failed') &&
                !u.paycenterDeactivated &&
                userDetail.kycCase?.status === 'CLOSED' &&
                userDetail.kycCase?.result === 'SUCCESS' && (
                  <Popconfirm
                    title="Register with PayCenter?"
                    description="This will create a PayCenter account for the user with FREE plan."
                    onConfirm={handleRegisterPayCenter}
                    okText="Yes, Register"
                    cancelText="Cancel"
                  >
                    <span>
                      <Btn tone="primary" icon={<Icon.bank />} size="sm" loading={registerPayCenterLoading}>
                        Register with PayCenter
                      </Btn>
                    </span>
                  </Popconfirm>
                )}

              <span style={{ width: 1, height: 20, background: 'var(--udm-border)', margin: '0 4px' }} />
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--udm-text-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  fontWeight: 600,
                  marginRight: 4,
                }}
              >
                Debug
              </span>
              <Btn icon={<Icon.arrowOut />} size="sm" onClick={openPosthog}>
                Posthog
              </Btn>

              <div style={{ flex: 1 }} />

              {isAdmin && (u.status === UserStatus.ACTIVE || u.status === UserStatus.DELETION_PENDING) && (
                <Popconfirm
                  title="Suspend User"
                  description={
                    <div style={{ width: 250 }}>
                      <Input
                        placeholder="Reason for suspension"
                        value={suspendReason}
                        onChange={e => setSuspendReason(e.target.value)}
                        onPressEnter={handleSuspendUser}
                      />
                    </div>
                  }
                  onConfirm={handleSuspendUser}
                  okText="Suspend"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true, loading: actionLoading }}
                >
                  <span>
                    <Btn tone="danger" icon={<Icon.lock />} size="sm" loading={actionLoading}>
                      Suspend User
                    </Btn>
                  </span>
                </Popconfirm>
              )}
              {isAdmin && u.status === UserStatus.SUSPENDED && (
                <Popconfirm title="Activate user?" onConfirm={handleActivateUser} okText="Yes" cancelText="No">
                  <span>
                    <Btn tone="primary" icon={<Icon.unlock />} size="sm" loading={actionLoading}>
                      Activate User
                    </Btn>
                  </span>
                </Popconfirm>
              )}
              {isAdmin && u.paycenterCustomerId && !u.paycenterDeactivated && (
                <Popconfirm
                  title="Deactivate PayCenter Account?"
                  description="This may be irreversible."
                  onConfirm={handleDeactivatePayCenter}
                  okText="Yes, Deactivate"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <span>
                    <Btn tone="danger" icon={<Icon.ban />} size="sm" loading={deactivateLoading}>
                      Deactivate PayCenter
                    </Btn>
                  </span>
                </Popconfirm>
              )}
            </footer>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--udm-text-3)' }}>
            No user data available
          </div>
        )}
      </div>

      {/* ── Sub-modals ── */}
      <SendNotificationModal
        visible={showNotificationModal}
        userId={userId}
        userInfo={{
          name: userDetail
            ? formatFullName(userDetail.user.firstName, userDetail.user.lastName, userDetail.user.title)
            : undefined,
          phoneNumber: userDetail?.user.phoneNumber,
        }}
        initialTitle={reminderTemplate ? REMINDER_TEMPLATES[reminderTemplate].title : undefined}
        initialBody={reminderTemplate ? REMINDER_TEMPLATES[reminderTemplate].body : undefined}
        onSent={() => queryClient.invalidateQueries({ queryKey: [PUSH_HISTORY_QUERY_KEY] })}
        onClose={() => {
          setShowNotificationModal(false);
          setReminderTemplate(null);
        }}
      />

      {userId && (
        <TradevestTaxModal
          userId={userId}
          userName={
            userDetail ? formatFullName(userDetail.user.firstName, userDetail.user.lastName, userDetail.user.title) : ''
          }
          isAdmin={isAdmin}
          open={showTradevestTaxModal}
          onClose={() => setShowTradevestTaxModal(false)}
        />
      )}

      <Modal
        title="PayCenter Card Properties (Raw Data)"
        open={showCardPropertiesModal}
        onCancel={() => setShowCardPropertiesModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowCardPropertiesModal(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <Tabs
          defaultActiveKey="validation"
          items={[
            {
              key: 'validation',
              label: 'Card Validation',
              children: (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                    {cardValidationData ? JSON.stringify(cardValidationData, null, 2) : 'No data available'}
                  </pre>
                </div>
              ),
            },
            {
              key: 'plastic',
              label: 'Plastic Card Status',
              children: (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                    {plasticCardSatisfiedData ? JSON.stringify(plasticCardSatisfiedData, null, 2) : 'No data available'}
                  </pre>
                </div>
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title="Edit User Details"
        open={showEditDetailsModal}
        onCancel={() => setShowEditDetailsModal(false)}
        onOk={handleEditDetailsSubmit}
        confirmLoading={editDetailsLoading}
        width={500}
      >
        <Form form={editDetailsForm} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: false }]}>
            <Select placeholder="Select title" allowClear>
              <Select.Option value={UserTitle.MR}>Mr.</Select.Option>
              <Select.Option value={UserTitle.MS}>Ms.</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'First name is required' }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last Name" rules={[{ required: true, message: 'Last name is required' }]}>
            <Input placeholder="Enter last name" />
          </Form.Item>
          <Form.Item
            name="taxId"
            label="Tax ID"
            extra={
              taxIdRequired
                ? `Required by Tradevest for tax residency ${u?.country}. Securities onboarding cannot start without it.`
                : 'Not required for German tax residents.'
            }
            normalize={value => (typeof value === 'string' ? normalizeTaxId(value) : value)}
            rules={[{ max: MAX_TAX_ID_LENGTH, message: `At most ${MAX_TAX_ID_LENGTH} characters` }]}
          >
            <Input placeholder="e.g. RSSMRA85T10A562S" autoComplete="off" />
          </Form.Item>
        </Form>
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
          Note: Name and title changes are synced to PayCenter if the user has an account there. The tax ID is not —
          PayCenter has no such field; it is sent to Tradevest at onboarding.
        </Typography.Text>
      </Modal>

      <Modal
        title="Change Email Address"
        open={showEditEmailModal}
        onCancel={() => setShowEditEmailModal(false)}
        onOk={handleEditEmailSubmit}
        okText="Change email"
        confirmLoading={editEmailLoading}
        width={520}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Current address: <Typography.Text strong>{u?.email || '—'}</Typography.Text>
          {u?.isEmailConfirmed ? ' (verified)' : ' (not verified)'}
        </Typography.Paragraph>
        <Form form={editEmailForm} layout="vertical">
          <Form.Item
            name="email"
            label="New Email Address"
            // antd's `type: 'email'` regex is anchored, so a pasted "  a@b.com " fails validation
            // instead of being trimmed. Normalising first is what makes the paste work.
            normalize={value => (typeof value === 'string' ? value.trim() : value)}
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email address' },
            ]}
          >
            <Input placeholder="name@example.com" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            // Without `whitespace`, async-validator accepts "   " and the server rejects it —
            // an opaque failure on a field the form said was valid.
            rules={[{ required: true, whitespace: true, message: 'A reason is required — it goes into the audit log' }]}
          >
            <Input.TextArea rows={2} placeholder="e.g. customer request via support ticket #123" />
          </Form.Item>
        </Form>
        <Alert
          type="warning"
          showIcon
          message="Read before confirming"
          description={
            <>
              {u?.isEmailConfirmed ? (
                <div>
                  The current address is verified, so the new one is marked <strong>verified immediately</strong> — the
                  user is not asked to confirm it. Only do this for a request you have established is genuine.
                </div>
              ) : (
                <div>
                  The current address was never verified, so the new one is stored <strong>unverified</strong> and a
                  code is sent to it. The user has to confirm it before invoices resume.
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                The change is <strong>not propagated</strong> to PayCenter, Tangany or Tradevest; they keep the old
                address on file.
              </div>
              {u?.isEmailConfirmed && (
                <div style={{ marginTop: 8 }}>A security notice is sent to the previous address.</div>
              )}
            </>
          }
        />
      </Modal>

      <Modal
        title="Tradevest Onboarding Retry"
        open={showTradevestRetryModal}
        onCancel={() => {
          setShowTradevestRetryModal(false);
          setTradevestRetryResult(null);
          fetchUserDetails();
          onUserUpdated?.();
        }}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              setShowTradevestRetryModal(false);
              setTradevestRetryResult(null);
              fetchUserDetails();
              onUserUpdated?.();
            }}
          >
            Close
          </Button>,
        ]}
        width={520}
      >
        {tradevestRetryLoading && !tradevestRetryResult ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Processing retry...</Text>
            </div>
          </div>
        ) : tradevestRetryResult ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text type="secondary">Status transition: </Text>
              <Tag color="orange">{tradevestRetryResult.previousStatus}</Tag>
              <Text type="secondary"> → </Text>
              <Tag
                color={
                  tradevestRetryResult.newStatus === 'completed'
                    ? 'green'
                    : tradevestRetryResult.newStatus === tradevestRetryResult.previousStatus
                      ? 'default'
                      : 'blue'
                }
              >
                {tradevestRetryResult.newStatus}
              </Tag>
            </div>
            <Timeline
              items={tradevestRetryResult.steps.map(step => ({
                color:
                  step.status === 'success'
                    ? 'green'
                    : step.status === 'error'
                      ? 'red'
                      : step.status === 'skipped'
                        ? 'gray'
                        : 'blue',
                dot:
                  step.status === 'success' ? (
                    <CheckCircleOutlined />
                  ) : step.status === 'error' ? (
                    <CloseCircleOutlined />
                  ) : step.status === 'skipped' ? (
                    <MinusCircleOutlined />
                  ) : (
                    <InfoCircleOutlined />
                  ),
                children: (
                  <div>
                    <Text strong>{step.label}</Text>
                    {step.detail && (
                      <div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {step.detail}
                        </Text>
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
            <Alert message={tradevestRetryResult.message} type="info" showIcon />
          </div>
        ) : null}
      </Modal>

      <Modal
        title="Set Tradevest Provisioning Status"
        open={showSetStatusModal}
        onCancel={() => {
          setShowSetStatusModal(false);
          setStatusForm.resetFields();
        }}
        onOk={handleSetTradevestStatus}
        okText="Apply Status"
        okButtonProps={{ loading: setStatusLoading, danger: true }}
        cancelText="Cancel"
        width={520}
      >
        <Alert
          message="Warning: Manual Override"
          description="This directly sets the provisioning status without triggering any automated flows. Use only when directed by Tradevest provider."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={setStatusForm} layout="vertical">
          <Form.Item name="status" label="Target Status" rules={[{ required: true, message: 'Select a status' }]}>
            <Select placeholder="Select status" showSearch optionFilterProp="label">
              {renderStatusOptGroup('Progress States', TRADEVEST_PROGRESS_STATUSES)}
              {renderStatusOptGroup('Failed States', TRADEVEST_FAILED_STATUSES)}
            </Select>
          </Form.Item>
          <Form.Item name="clearError" valuePropName="checked">
            <Checkbox>Clear provisioning error</Checkbox>
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'Provide a reason for this override' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="e.g., Tradevest directive: re-do document signing after NP status correction"
            />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tradevest hero stats — portfolio total, 24h delta, status pill.
// ─────────────────────────────────────────────────────────────────────────────
function TradevestHero({
  portfolio,
  loading,
  error,
}: {
  portfolio: TradevestUserPortfolioSummary | null;
  loading: boolean;
  error: string | null;
}) {
  const fmt = (n: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);

  const change = portfolio?.change24h;
  const hasChange = change?.available;
  const positive = hasChange && change.deltaEur > 0;
  const negative = hasChange && change.deltaEur < 0;
  const sign = positive ? '+' : negative ? '−' : '';
  const changeColor = positive ? 'var(--udm-green)' : negative ? 'var(--udm-red)' : 'var(--udm-text-3)';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 14,
        padding: 14,
        background: 'linear-gradient(180deg, #fafbfc, #f4f6f9)',
        border: '1px solid var(--udm-border)',
        borderRadius: 10,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--udm-text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
          }}
        >
          Portfolio
        </div>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
          {loading ? <Spin size="small" /> : error ? '—' : portfolio ? fmt(portfolio.totalValueEur) : '—'}
        </div>
        {error && <div style={{ fontSize: 11, color: 'var(--udm-red)' }}>{error}</div>}
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--udm-text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
          }}
        >
          24h Change
        </div>
        {hasChange ? (
          <>
            <div
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: changeColor,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {positive ? <Icon.arrowUp /> : negative ? <Icon.arrowDown /> : null}
              {sign}
              {fmt(Math.abs(change.deltaEur))}
            </div>
            <div style={{ fontSize: 12, color: changeColor, fontWeight: 500 }}>
              {sign}
              {Math.abs(change.percent).toFixed(2)}%
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: 'var(--udm-text-3)' }}>Not enough history yet</div>
        )}
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            color: 'var(--udm-text-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
          }}
        >
          Status
        </div>
        <div style={{ marginTop: 2 }}>
          <Pill tone="green" icon={<Icon.check />}>
            Completed
          </Pill>
        </div>
      </div>
    </div>
  );
}
